// App.tsx
import { AppRouter } from './routes';   // This resolves to index.tsx if that's the only index file
import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { Toaster as HotToaster } from 'react-hot-toast';
import { Toaster as SonnerToaster } from 'sonner';
import { ServiceProvider } from "@/features/premium/Marketplace/ServiceProvider";
import { ClientProvider } from '@/contexts/ClientContext';
import { CaseProvider } from '@/contexts/CaseContext';
import { ToolRegistryProvider } from '@/contexts/ToolRegistryContext';
import { AgencyProvider } from '@/contexts/AgencyContext';
import { I18nProvider } from './i18n/i18n';
import { UserProfileProvider } from "./contexts/UserProfileContext.emergency";
import { TierProvider } from "./contexts/TierContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { NavigationProvider } from "./components/navigation/NavigationProvider";
import { AuthProvider } from "./contexts/AuthContext";
import { MemoryProvider } from "@/contexts/MemoryContext";
import { JourneyMemoryProvider } from "@/contexts/JourneyMemoryContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { QuantumPageLoader } from './components/QuantumLoadingStates';
import AppLayout from '@/components/layout/AppLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Clean up any problematic localStorage keys
    const problematicKeys = ['last_path', 'redirect_count', 'auth_loop_detected', 'infinite_loop_guard'];
    problematicKeys.forEach(key => localStorage.removeItem(key));
    
    // FIX: Force onboarding as completed for all users to prevent redirect loops
    // This ensures users don't get stuck in the onboarding flow
    localStorage.setItem('onboarding_completed', 'true');
    
    setIsReady(true);
  }, []);

  if (!isReady) {
    return <QuantumPageLoader message="Initializing system..." />;
  }

  return (
    <ErrorBoundary>
      <div className="dark">
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <Router>
              <AuthProvider>
                <MemoryProvider>
                  <JourneyMemoryProvider>
                  <UserProfileProvider>
                  <AgencyProvider>
                    <ClientProvider>
                      <CaseProvider>
                        <ToolRegistryProvider>
                          <NavigationProvider>
                            <TierProvider>
                              <I18nProvider>
                                <Suspense fallback={<QuantumPageLoader message="Loading application..." />}>
                                  <HotToaster position="top-right" />
                                  <SonnerToaster position="top-center" richColors closeButton />
                                  <AppLayout>
                                    <Toaster />
                                    <AppRouter />
                                  </AppLayout>
                                </Suspense>
                              </I18nProvider>
                            </TierProvider>
                          </NavigationProvider>
                        </ToolRegistryProvider>
                      </CaseProvider>
                    </ClientProvider>
                  </AgencyProvider>
                  </UserProfileProvider>
                </JourneyMemoryProvider>
                </MemoryProvider>
              </AuthProvider>
            </Router>
          </LanguageProvider>
        </QueryClientProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;